/**
 * Custom hook for Supplier Performance state management
 *
 * Owns the ranked list (fetch, filters, paging, export). The drill-down
 * modal — what each cell opens, its paging and its export — lives in
 * useDrilldownModal and is spread into this hook's return unchanged.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  suppliersAPI,
  fetchAllPages,
  SupplierProfile,
  SupplierSelfPerformanceRow,
} from '../../lib/api';
import { useRatingBands } from '../../hooks/useRatingBands';
import { useOTIFBands } from '../../hooks/useOTIFBands';
import { SupplierPerformanceFilterValues } from '../../components/suppliers/SupplierPerformanceFilters';
import { useFormatters, usePreferences } from '../../contexts/PreferencesContext';
import { useView } from '../../contexts/ViewContext';
import { usePortalMode } from '../../hooks/usePortalMode';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';
import { usePagination } from '../../hooks';
import { getErrorMessage } from '../../hooks/useFetchData';
import { DEFAULT_FILTERS } from './constants';
import { getGradeInfo, isNARating } from './utils';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import { useDrilldownModal } from './useDrilldownModal';
import { buildExportScopeRows, ExportScopeRow } from './performanceExportRows';
import { sortParam, sortSelfRows, usePerformanceSort } from './performanceSort';
import { ratingFilterParams } from './ratingFilter';
import {
  buildSelfParams,
  filterSelfRows,
  keySelfRows,
  selfFilterOptions,
  selfRowOf,
  selfRowGrade,
} from './selfPerformance';

export function useSupplierPerformance() {
  const { preferences } = useFormatters();
  // The request carries `currency`; wait for the tenant's effective
  // preference so the first ranked-list fetch is not issued with the
  // platform-default currency and then repeated (~3s of server compute per
  // page visit) once the real one arrives.
  const { isLoading: prefsLoading } = usePreferences();
  const qualityLabel = useQualityMeasureLabel();
  const { getViewQueryParams, isLoading: viewContextLoading } =
    useView();
  // SI-16: a supplier user gets the supplier-side page — own scores, one row
  // per customer, from /profiles/by-customer/ instead of the ranked list.
  // A helix user browsing a dual-mode tenant's supplier view is still helix to
  // the backend (403 on by-customer), so they keep the customer page.
  const { isSupplier: portalIsSupplier, isHelix } = usePortalMode();
  // CR-4547: the Excel export carries the Warehouse column exactly when the
  // table does (tenant Warehouse filter visibility, CR-4802).
  const { warehouse: showWarehouseColumn } = useFilterVisibility();
  const isSupplier = portalIsSupplier && !isHelix;
  const [selfRows, setSelfRows] = useState<SupplierSelfPerformanceRow[]>([]);

  // Core states
  const [suppliers, setSuppliers] = useState<SupplierProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isMatrixView, setIsMatrixView] = useState(false);
  const [filters, setFilters] = useState<SupplierPerformanceFilterValues>(DEFAULT_FILTERS);
  const [initialLoad, setInitialLoad] = useState(true);

  // Pagination
  const pagination = usePagination({ pageSize: 20, scrollToTop: true });
  // CR-8667: column sort; a new sort starts again at page 1.
  const { sort, onSort } = usePerformanceSort(pagination.reset);

  // Drill-down modal (state, openers, paging, export)
  const modal = useDrilldownModal({
    isSupplier,
    selfRows,
    filters,
    getViewQueryParams,
    currency: preferences.currency,
  });

  // Customer review 11 Sep (item 2): the configured Grade Thresholds bands
  // for every customer-side role. This read the admin-only settings endpoint,
  // so a Customer User got a 403 and graded on the hard-coded fallback scale.
  // SI-16: suppliers get [] — each supplier-side row carries its customer's bands.
  const { bands: ratingThresholds } = useRatingBands();
  // CR-6269: the OTIF % column is coloured on the same OTIF Grade Thresholds
  // the dashboard tiles use, not a fixed 90/80 traffic light.
  const { bands: otifBands } = useOTIFBands();

  // Debounce search input - wait 400ms after user stops typing
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm]);

  // Fingerprint of the filter VALUES — used by the pagination-reset effect
  // below so an object-identity re-apply doesn't bounce the page.
  const filtersKey = JSON.stringify(filters);

  // Build the supplier-profile query params for the current filter set.
  // Shared by the paginated fetch and the "export all" path (which passes a
  // large page size so the export covers every matching supplier, not just the
  // current page — CR: export was limited to the 10/50/100 page size).
  const buildProfileParams = (page: number, pageSize: number): Record<string, string | number | boolean> => {
    const params: Record<string, string | number | boolean> = {
      page,
      page_size: pageSize,
      currency: preferences.currency,
    };
    const viewParams = getViewQueryParams();
    if (viewParams['group']) params['group'] = viewParams['group'];
    if (viewParams['company']) params['company'] = viewParams['company'];
    if (viewParams['site']) params['site'] = viewParams['site'];
    // CR-7264 rework: the Company Site filter never reached this request — it
    // was a silent no-op exactly like the Supplier Group one. An explicit pick
    // narrows within (and so overrides) the header view scope.
    if (filters.site && filters.site !== 'all') params['site'] = filters.site;
    if (debouncedSearchTerm) params['search'] = debouncedSearchTerm;
    if (filters.division !== 'all') params['division'] = filters.division;
    if (filters.supplierCode) params['supplier_code__icontains'] = filters.supplierCode;
    // Customer review 11 Sep: the Warehouse filter never reached this request
    // (the matrix and the drill-downs sent it), so picking one changed nothing.
    if (filters.warehouse && filters.warehouse !== 'all') params['warehouse'] = filters.warehouse;
    if (filters.strategic) params['is_preferred'] = true;
    if (filters.supplierGroup !== 'all') params['company__group'] = filters.supplierGroup;
    if (filters.commodityGroup !== 'all') params['commodity_group'] = filters.commodityGroup;
    if (filters.commodityType !== 'all') params['commodity_type'] = filters.commodityType;
    if (filters.dateFrom) params['date_from'] = filters.dateFrom;
    if (filters.dateTo) params['date_to'] = filters.dateTo;
    // W3-T9: the Historical/Live switch decides the arrears term here as on
    // the dashboard (Live = today's open overdue lines; default Historical).
    if (filters.dataMode === 'live') params['data_mode'] = 'live';
    if (filters.excludeNARatings) params['exclude_na_ratings'] = 'true';
    // CR-6506: only suppliers that have passed onboarding — the Suppliers
    // page's rule — whatever the Exclude N/A checkbox says. Every request this
    // page makes (table, site and code rows, export) derives from here, so
    // the gate cannot be dropped on one tier and kept on another.
    params['approved_only'] = 'true';
    // CR-8654: a supplier switched out of all three KPIs (KPI Settings) has no
    // score to show and is not listed — table, count, pages and export alike.
    params['kpi_included_only'] = 'true';
    if (filters.supplierSite !== 'all') params['company_id'] = filters.supplierSite;
    // CR-4783: Geographic + supplier-type filters
    if (filters.siteCountry && filters.siteCountry !== 'all') params['site_country'] = filters.siteCountry;
    if (filters.supplierCountry && filters.supplierCountry !== 'all') params['supplier_country'] = filters.supplierCountry;
    if (filters.supplierType && filters.supplierType !== 'all') params['supplier_type'] = filters.supplierType;
    // CR-8667: sorted server-side over every matching supplier, then paged —
    // so the export, which walks these pages, follows the sort on screen too.
    const sortValue = sortParam(sort);
    if (sortValue) params['sort'] = sortValue;
    // CR-8667: the Rating filter is applied server-side too, before the sort
    // and the page, graded on the same bands as the Rating column.
    Object.assign(params, ratingFilterParams(filters.rating, ratingThresholds));
    return params;
  };

  // Customer review 11 Sep (item 3/4): a row's per-customer-site breakdown
  // asks the SAME list endpoint for one site × one supplier — every filter and
  // the date window unchanged, the view scope replaced by that site — so each
  // breakdown row is computed exactly as the page would compute it at that
  // site (the backend intersects `site` with the viewer's grant).
  const buildSiteRowParams = (siteId: number, supplierId: number): Record<string, string | number | boolean> => {
    const params = buildProfileParams(1, 1);
    delete params['group'];
    delete params['company'];
    delete params['division'];
    // A site where the supplier has no rating still gets its row (shown N/A).
    delete params['exclude_na_ratings'];
    // CR-8667: one supplier at one site — nothing to sort, and the site row is
    // shown under its supplier whatever that site's own grade (as before).
    delete params['sort'];
    delete params['rating'];
    delete params['rating_bands'];
    params['site'] = siteId;
    params['company_id'] = supplierId;
    return params;
  };

  /**
   * One code's row under a site — CR-7288's third tier.
   *
   * The same request as the site row above it with the code added, so the
   * figures are computed by the same definitions and sum to it. `code` of
   * null asks for the `(no code)` bucket: the lines carrying no code at all,
   * which is the rest of the site's total and why the rows add up.
   */
  const buildCodeRowParams = (
    siteId: number, supplierId: number, code: string | null,
  ): Record<string, string | number | boolean> => {
    const params = buildSiteRowParams(siteId, supplierId);
    if (code === null) params['supplier_code_none'] = 'true';
    else params['supplier_code'] = code;
    return params;
  };

  // The complete signature of the request fetchData would issue right now.
  // The fetch effect keys on THIS, not on the raw state it derives from:
  // several upstream sources change state without changing the request —
  // the hierarchy-scope sync writes filter fields that resolve to the same
  // request, and ViewContext back-fills ids that resolve to the same
  // query params — and each such change refired the fetch with a
  // byte-identical URL (~3.5s of duplicate ranked-list compute per page
  // visit, measured on production). Keying on the derived request makes
  // "would the server see a different call?" the exact refetch condition.
  const requestKey = JSON.stringify(isSupplier
    // getViewQueryParams() is in the key so a site switch in the header (which
    // changes the persisted current_site the backend resolves) refetches.
    ? ['self', buildSelfParams(filters, preferences.currency), getViewQueryParams()]
    : [buildProfileParams(pagination.currentPage, pagination.pageSize), getViewQueryParams()]);

  // Fetch data when the outgoing request changes
  useEffect(() => {
    if (viewContextLoading || prefsLoading) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewContextLoading, prefsLoading, requestKey]);

  // Monotonic fetch id: a superseded request (the user changed a filter, or
  // the currency resolved, while the ranked list was still computing) must
  // not overwrite a newer one when it finally lands out of order.
  const fetchSeqRef = useRef(0);
  const fetchData = async () => {
    const seq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      setError(null);

      if (isSupplier) {
        const resp = await suppliersAPI.getByCustomer(buildSelfParams(filters, preferences.currency));
        if (seq !== fetchSeqRef.current) return;
        setSelfRows(keySelfRows(resp.data.results));
        return;
      }

      // CR-8667: the list alone. This also fetched dashboard_stats on every
      // request — every sort click and page turn — for a result the page never
      // read, and a failure of it failed the whole table.
      const params = buildProfileParams(pagination.currentPage, pagination.pageSize);
      const profilesResponse = await suppliersAPI.getProfiles(params);
      if (seq !== fetchSeqRef.current) return;

      setSuppliers(profilesResponse.data.results);
      pagination.setFromResponse(profilesResponse.data.count, (profilesResponse.data.results || []).length);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      // If page is out of range (404), reset to page 1 — the useEffect will refetch
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 && pagination.currentPage > 1) {
        pagination.reset();
        return;
      }
      console.error('Error fetching performance data:', err);
      setError(getErrorMessage(err, 'Failed to load performance data'));
    } finally {
      if (seq === fetchSeqRef.current) {
        setLoading(false);
        setInitialLoad(false);
      }
    }
  };

  // CR-8667: the Rating filter is applied server-side (ratingFilterParams in
  // buildProfileParams), like N/A exclusion, so the count and the pages agree;
  // it used to filter the 20 rows on screen here.
  // SI-16: supplier-side rows are filtered and paged client-side (the server
  // returns every customer; there are only a handful).
  const selfFiltered = isSupplier ? sortSelfRows(filterSelfRows(selfRows, filters, debouncedSearchTerm), sort) : [];
  useEffect(() => {
    if (isSupplier) pagination.setFromResponse(selfFiltered.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSupplier, selfFiltered.length]);

  const filteredSuppliers: SupplierProfile[] = isSupplier ? selfFiltered.slice(
    (pagination.currentPage - 1) * pagination.pageSize,
    pagination.currentPage * pagination.pageSize,
  ) : suppliers;

  // Reset pagination when filters change (value-keyed, same as the fetch
  // effect — an identity-only re-apply must not bounce the page).
  useEffect(() => {
    pagination.reset();
  }, [debouncedSearchTerm, filtersKey]);

  // Apply filters — receives the pending filters from the filter component and triggers a fetch
  const handleApplyFilters = useCallback((newFilters: SupplierPerformanceFilterValues) => {
    setFilters(newFilters);
    pagination.reset();
    // The useEffect watching [filters, pagination.currentPage] will fire and call fetchData
  }, []);

  // The export walks every page: the server caps page_size at 1000, so the old
  // single page_size=100000 request silently stopped at the first thousand.
  // Neither the table nor the export sends `ordering`: `-overall_rating` alone
  // leaves ties in no fixed order, so a supplier could land on two pages and
  // another on none. The server default sorts by rating and breaks ties by name, id.
  const exportPageParams = (params: Record<string, string | number | boolean>) => (
    { ...params, page_size: 1000 }
  );

  // Export to Excel — ALL suppliers matching the current filters, not just the
  // current page. Pulls the full set server-side (every page), already
  // filtered by rating and sorted as the table is (CR-8667).
  const handleExportExcel = useCallback(async () => {
    let rows: SupplierProfile[];
    try {
      rows = isSupplier
        ? sortSelfRows(filterSelfRows(selfRows, filters, debouncedSearchTerm), sort)
        : await fetchAllPages<SupplierProfile>((page) => suppliersAPI.getProfiles(
          exportPageParams(buildProfileParams(page, 1000))))
          // Each page is cached on its own, so one fetched later can repeat a row.
          .then((all) => Array.from(new Map(all.map((s) => [s.id, s])).values()));
    } catch {
      toast.error('Failed to load suppliers for export');
      return;
    }
    if (rows.length === 0) {
      toast.info('No data to export');
      return;
    }

    // CR-7288: the sheet now carries the page's three tiers, flat. A supplier
    // viewer has no customer-site tier to break down, so it keeps the old
    // one-row-per-supplier shape.
    let scopeRows: ExportScopeRow[];
    if (isSupplier) {
      scopeRows = rows.map((supplier) => ({
        supplier, level: 'Supplier' as const, siteName: '', code: '',
      }));
    } else {
      try {
        scopeRows = await buildExportScopeRows(
          rows,
          // Round 1 (blind stage 2): this used to start from
          // buildProfileParams, while the SCREEN's site rows start from
          // buildSiteRowParams — which drops group/company/division and
          // exclude_na_ratings. With "exclude N/A ratings" on, the export
          // therefore lost any site where the supplier has no rating, and
          // every code row under it, while the screen showed that site as N/A.
          // One builder, so the file and the page cannot disagree.
          (extra) => {
            // The screen asks per supplier; the export asks per SCOPE and gets
            // every supplier back, so the supplier narrowing comes straight
            // out again. Everything else — the dropped group/company/division
            // and exclude_na_ratings — is what makes the two agree.
            const params = buildSiteRowParams(Number(extra['site'] ?? 0), 0);
            delete params['company_id'];
            return { ...exportPageParams(params), ...extra };
          },
        );
      } catch {
        toast.error('Failed to load the site and code breakdown for export');
        return;
      }
    }

    const exportData = scopeRows.map(({ supplier, level, siteName, code }) => ({
      'Supplier Name': supplier.company_name,
      ...(isSupplier ? { 'Supplier Site': selfRowOf(supplier)?.supplier_site_name ?? '' } : {}),
      // CR-7288: filter Level to "Supplier" to get the export exactly as it was
      // before this, or pivot on Supplier Code for the breakdown.
      ...(isSupplier ? {} : { Level: level, 'Customer Site': siteName, 'Supplier Code': code }),
      // CR-4547: no Warehouse column in the file when the tenant hides it on screen.
      ...(showWarehouseColumn ? { Warehouse: supplier.warehouse_name || '-' } : {}),
      // Customer review 11 Sep (msg 3): every site the row covers, not the first.
      'Customer Companies': supplier.customers || '-',
      Rating: isNARating(supplier)
        ? 'N/A'
        : (() => { const self = selfRowOf(supplier); return self ? selfRowGrade(self) : getGradeInfo(supplier.combined_score ?? undefined, ratingThresholds).label; })(),
      'Qty Received': supplier.qty_received || 0,
      'Qty Rejected': supplier.reject_qty || 0,
      [`Customer Liability ${qualityLabel}`]: supplier.customer_liability_ppm?.toFixed(2) || 0,
      [`Supplier Liability ${qualityLabel}`]: supplier.supplier_liability_ppm?.toFixed(2) || 0,
      [`Total ${qualityLabel}`]: supplier.total_ppm?.toFixed(2) || 0,
      'No. of NCRs': supplier.ncr_count || 0,
      // SI-16: pooled across customers — omitted on the supplier-side export.
      ...(isSupplier ? {} : { 'Response Score': supplier.response_score || 0 }),
      'Total Value': supplier.total_value || 0,
      'Reject Value': supplier.reject_value || 0,
      // Numbers, not the API's decimal strings, so Excel can sum them (msg 10).
      'Customer Cost of Quality': Number(supplier.customer_cost_of_quality || 0),
      'Supplier Cost of Quality': Number(supplier.supplier_cost_of_quality || 0),
      'Total Cost of Quality': Number(supplier.total_cost_of_quality || 0),
      'PO Lines': supplier.po_line_count || 0,
      OTIF: supplier.otif_count || 0,
      Arrears: supplier.arrears_count || 0,
      'OTIF Score %': supplier.otif_score_percentage == null ? 'N/A' : supplier.otif_score_percentage.toFixed(1),
      'Qty Diff': supplier.qty_diff || 0,
      'No. of GRNs': supplier.grn_count || 0,
      Ontime: supplier.ontime_count || 0,
      Late: supplier.late_count || 0,
      Early: supplier.early_count || 0,
      // Customer review 11 Sep (msg 16): CO2 is now scoped to the row's customer,
      // so the supplier-side export carries it too.
      // CR-7288 round 2: null is NOT zero here. CO2 is captured per shipment
      // and a shipment can cover more than one code, so a CODE row's CO2 is
      // null BY DESIGN — writing it as 0 says "none was emitted", which is a
      // different and false claim. 'N/A' matches what the screen shows.
      'CO2 Emissions': supplier.carbon_emission == null ? 'N/A' : supplier.carbon_emission,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Supplier Performance');

    const date = new Date().toISOString().split('T')[0];
    const filename = `Supplier_Performance_${date}.xlsx`;
    XLSX.writeFile(workbook, filename);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, ratingThresholds, qualityLabel, preferences.currency, debouncedSearchTerm, isSupplier, selfRows, showWarehouseColumn, sort]);

  // Toggle matrix view
  const handleMatrixView = useCallback(() => {
    setIsMatrixView(!isMatrixView);
  }, [isMatrixView]);

  return {
    // Data
    suppliers,
    filteredSuppliers,
    ratingThresholds,
    otifBands,
    // SI-16: supplier-side page
    isSupplier,
    selfOptions: selfFilterOptions(selfRows),

    // UI states
    loading,
    initialLoad,
    error,
    searchTerm,
    isMatrixView,
    filters,

    // Pagination
    pagination,

    // CR-8667: column sort
    sort,
    onSort,

    // Modal (state, setModalOpen, page change, export, openers)
    ...modal,

    // Currency
    currency: preferences.currency,

    // Setters
    setSearchTerm,

    // Actions
    fetchData,
    handleApplyFilters,
    handleExportExcel,
    handleMatrixView,
    // Customer review 11 Sep (msg 3/4): per-customer-site breakdown requests
    buildSiteRowParams,
    buildCodeRowParams,
  };
}
